import { FC, Fragment, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip, IconButton, Box } from '@mui/material';
import { tableStyles } from '../Reports';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { fetchLedgerAction, fetchLedgerResetAction } from '../../../store/ledger';
import { getShortFormattedDate, currencyFormatter } from '../../../utils';

export interface LedgerProps {
  bill: any;
}

export const LedgerSummary: FC<LedgerProps> = ({ bill }) => {
  const classes = tableStyles();
  const dispatchAction = useAppDispatch();

  const { data: ledgerResponse } = useAppSelector((state) => state.fetchLedger);

  useEffect(() => {
    dispatchAction(fetchLedgerResetAction());
  }, []);

  useEffect(() => {
    if (bill) {
      dispatchAction(fetchLedgerAction({ accountId: bill.id }));
    }
  }, [bill]);

  const entries = ledgerResponse?.entries ?? [];
  const closingBalanceEntry = entries.find((e) => e.title === 'Closing Balance');
  const tableEntries = entries.filter((e) => e.title !== 'Closing Balance');
  const summaryDebit = ledgerResponse?.invoicesTotal ?? 0;
  const summaryCredit = ledgerResponse?.paymentTotal ?? 0;
  const summaryTds = ledgerResponse?.tdsTotal ?? 0;
  const summaryBalance = ledgerResponse?.totalDue ?? closingBalanceEntry?.balance ?? 0;

  let count = 0;

  return (
    <Box sx={{ mt: 0, pt: 0, width: '100%' }}>
      {ledgerResponse != null && (
        <TableContainer sx={{ maxWidth: '100%', mb: 1.5 }}>
          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }} aria-label="ledger summary">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#cccccc', color: 'black' }}>
                <TableCell className={classes.tableCell} sx={{ color: 'black', width: '25%' }}>Invoices & Refunds</TableCell>
                <TableCell className={classes.tableCell} sx={{ color: 'black', width: '25%' }}>Payments</TableCell>
                <TableCell className={classes.tableCell} sx={{ color: 'black', width: '25%' }}>TDS</TableCell>
                <TableCell className={classes.tableCell} sx={{ color: 'black', width: '25%' }}>Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={classes.tableCell} >{currencyFormatter.format(summaryDebit)}</TableCell>
                <TableCell className={classes.tableCell} >{currencyFormatter.format(summaryCredit)}</TableCell>
                <TableCell className={classes.tableCell} >{summaryTds > 0 ? currencyFormatter.format(summaryTds) : '-'}</TableCell>
                <TableCell className={classes.tableCell} sx={{ fontWeight: 600, color: summaryBalance <= 0 ? 'green' : 'red' }} data-cy="id_ledger_closing_balance_debit">{currencyFormatter.format(summaryBalance)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table aria-label="ledger report" sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#cccccc', color: 'black' }}>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '4%' }}>S.N.</TableCell>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '11%' }}>Date</TableCell>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '22%' }}>Description</TableCell>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '10%' }}>Entry Type</TableCell>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '12%' }}>Invoice No.</TableCell>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '12%' }}>Invoices</TableCell>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '12%' }}>Payments</TableCell>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '10%' }}>TDS</TableCell>
              <TableCell className={classes.tableCell} sx={{ color: 'black', width: '7%', minWidth: 48 }}>PDF</TableCell>
            </TableRow>
          </TableHead>
        <TableBody>
          {tableEntries.map((entry, index) => {
            const isSum = entry.title === 'Sum';
            const currentCount = isSum ? 0 : ++count;
            const rowId = isSum ? 'SUM' : currentCount.toString();

            return (
              <Fragment key={index}>
                <TableRow sx={{ backgroundColor: isSum ? '#f0f0f1' : 'white' }}>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_sn`}
                  >
                    {isSum ? '' : currentCount}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_date`}
                  >
                    {isSum ? '' : (entry.entryDate ? getShortFormattedDate(entry.entryDate) : '')}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_particular`}
                  >
                    {entry.title}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_entryType`}
                  >
                    {entry.entryType}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_refNo`}
                  >
                    {entry.invoiceNo || '-'}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_debit`}
                  >
                    {entry.debitAmount > 0 ? entry.debitAmount : '-'}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_credit`}
                  >
                    {entry.creditAmount > 0 ? entry.creditAmount : '-'}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_tds`}
                  >
                    {isSum
                      ? (() => {
                          const tdsTotal = entry.tdsAmount ?? ledgerResponse?.tdsTotal;
                          return tdsTotal != null && tdsTotal > 0 ? currencyFormatter.format(tdsTotal) : '-';
                        })()
                      : (entry.tdsAmount != null && entry.tdsAmount > 0 ? entry.tdsAmount : '-')}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: isSum ? 'bold' : 400 }}
                    className={classes.tableCell}
                    data-cy={`id_ledger_${rowId}_actions`}
                  >
                    {entry.pdfLink && (
                      <Tooltip title="View Bill" arrow>
                        <IconButton sx={{ p: 0, m: 0 }} onClick={() => window.open(entry.pdfLink, '_blank')}>
                          <PictureAsPdfIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      </TableContainer>
    </Box>
  );
};

export default LedgerSummary;
